import {
  useEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactElement,
  type UIEvent,
  type WheelEvent,
} from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { Notification as NotificationRowData, NotificationPage } from '@molt/shared';
import { Button, EmptyState, Notice, Skeleton } from '@/components';
import {
  getNotifications,
  notificationsQueryOptions,
  useMarkAllNotificationsRead,
  useSetNotificationRead,
} from '@/lib/notifications';
import { queryKeys } from '@/lib/query-keys';
import { useInterfaceLanguage } from '@/lib/interface-language';
import { NotificationPreferencesPanel } from './NotificationPreferencesPanel';
import { NotificationRow } from './NotificationRow';

/**
 * `FE-17.4` / `FR-NOTIF-009` AC3 — the drawer owns the **navigation seam**, so
 * it owns the one classification that only matters at the moment of the click.
 *
 * `notificationBoardRefSchema.readable` is `false` when the recipient can no
 * longer read the board the row is about — a former member of a live board, or
 * a board soft-deleted under them. Before this field the wire could not say it:
 * `closed` and `deleted` are facts about the *board* and neither says anything
 * about *this reader*, so a removed member saw `closed: false, deleted: false`
 * and a link that opened a 404.
 *
 * ⚠ **The flag classifies; it must never filter.** `FR-NOTIF-001` AC3 requires
 * the *removed from the board* notification itself to arrive and render, and
 * `FR-NOTIF-009` AC3 then requires the click to say "you no longer have access"
 * *rather than a blank board*. So the row is still listed, still readable, still
 * markable read — only the navigation is refused, and the refusal is announced.
 *
 * ⚠ **This closes the left-click path only.** `NotificationRow` renders a real
 * `<a href>` and calls `preventDefault()` only on a plain left click, so a
 * middle click, a cmd-click or "open in new tab" still follows the raw href past
 * this guard. Suppressing the anchor itself for an unreadable board is
 * `NotificationRow.tsx`'s and is deliberately not reached from here.
 */
function isUnreachableBoard(row: NotificationRowData): boolean {
  return row.board !== null && !row.board.readable;
}

export interface NotificationDrawerProps {
  readonly open: boolean;
  readonly unreadCount?: number;
}

export function NotificationDrawer({
  open,
  unreadCount,
}: NotificationDrawerProps): ReactElement {
  const { t } = useInterfaceLanguage();
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [accessRefused, setAccessRefused] = useState(false);
  const loadingMoreRef = useRef(false);
  const queryClient = useQueryClient();
  const notifications = useQuery({
    ...notificationsQueryOptions({ unread: unreadOnly }),
    enabled: open,
  });
  const setRead = useSetNotificationRead();
  const markAllRead = useMarkAllNotificationsRead();

  const page = notifications.data;

  /* The refusal is about one click, not about the drawer. Closing the drawer
     retires it, the same way navigating away or changing the filter does. */
  useEffect(() => {
    if (!open) setAccessRefused(false);
  }, [open]);

  const unreadLabel =
    unreadCount === undefined
      ? t('notifications.unreadCountLoading')
      : unreadCount === 1
        ? t('notifications.unreadCount', { count: 1 }, 1)
        : t('notifications.unreadCount', { count: unreadCount }, unreadCount);
  const hasNoUnread = unreadCount === 0;

  async function loadMore() {
    if (page?.nextCursor === null || page?.nextCursor === undefined) return;
    if (loadingMoreRef.current) return;
    const cursor = page.nextCursor;
    loadingMoreRef.current = true;
    setLoadingMore(true);
    try {
      const next = await getNotifications({
        cursor,
        limit: 25,
        unread: unreadOnly,
      });
      queryClient.setQueryData<NotificationPage>(
        queryKeys.notifications.list(unreadOnly),
        (current) => {
          if (current !== undefined && current.nextCursor !== cursor) return current;
          const base = current ?? page;
          const knownIds = new Set(base.notifications.map((row) => row.id));
          return {
            ...base,
            nextCursor: next.nextCursor,
            notifications: [
              ...base.notifications,
              ...next.notifications.filter((row) => !knownIds.has(row.id)),
            ],
          };
        },
      );
    } finally {
      loadingMoreRef.current = false;
      setLoadingMore(false);
    }
  }

  function handleHistoryScroll(event: UIEvent<HTMLDivElement>) {
    const target = event.currentTarget;
    const remaining = target.scrollHeight - target.scrollTop - target.clientHeight;
    if (remaining <= 48) {
      void loadMore();
    }
  }

  function handleHistoryWheel(event: WheelEvent<HTMLDivElement>) {
    if (event.deltaY <= 0) return;
    const target = event.currentTarget;
    const remaining = target.scrollHeight - target.scrollTop - target.clientHeight;
    if (remaining <= 48) {
      void loadMore();
    }
  }

  function handleHistoryKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    if (!['ArrowDown', 'End', 'PageDown', ' '].includes(event.key)) return;
    const target = event.currentTarget;
    const remaining = target.scrollHeight - target.scrollTop - target.clientHeight;
    if (remaining <= 48) {
      void loadMore();
    }
  }

  return (
    <div className="flex h-full min-h-64 flex-col">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border px-3 py-2">
        <div className="flex flex-wrap items-center gap-1">
          <Button
            size="sm"
            variant={unreadOnly ? 'primary' : 'quiet'}
            aria-pressed={unreadOnly}
            onClick={() => {
              setAccessRefused(false);
              setUnreadOnly((value) => !value);
            }}
          >
            {t('notifications.unreadOnly')}
          </Button>
          <Button
            size="sm"
            variant={settingsOpen ? 'primary' : 'quiet'}
            aria-pressed={settingsOpen}
            onClick={() => setSettingsOpen((value) => !value)}
          >
            {t('notifications.settings')}
          </Button>
        </div>
        <span className="text-xs text-ink-muted">{unreadLabel}</span>
        <Button
          size="sm"
          variant="quiet"
          busy={markAllRead.isPending}
          disabled={hasNoUnread}
          disabledReason={hasNoUnread ? t('notifications.noUnread') : undefined}
          onClick={() => {
            markAllRead.mutate();
          }}
        >
          {t('notifications.markAllRead')}
        </Button>
      </div>

      <NotificationPreferencesPanel open={settingsOpen} />

      {accessRefused ? (
        <div className="px-2 pt-2">
          <Notice tone="warn" body={t('card.notAvailable.body')} />
        </div>
      ) : null}

      <div
        role="region"
        aria-label={t('notifications.history')}
        tabIndex={0}
        className="flex min-h-0 flex-1 flex-col overflow-y-auto overscroll-contain px-2 py-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
        onScroll={handleHistoryScroll}
        onWheel={handleHistoryWheel}
        onKeyDown={handleHistoryKeyDown}
      >
        {notifications.isLoading ? (
          <div aria-busy="true" aria-label={t('notifications.loading')}>
            <Skeleton shape="row" count={5} />
          </div>
        ) : notifications.isError || page === undefined ? (
          <Notice
            tone="bad"
            title={t('notifications.loadFailedTitle')}
            body={t('notifications.tryOpenAgain')}
          />
        ) : page.notifications.length === 0 ? (
          <EmptyState
            tone="neutral"
            size="inline"
            title={
              unreadOnly ? t('notifications.allCaughtUp') : t('notifications.noneYet')
            }
            body={
              unreadOnly
                ? t('notifications.noUnreadLast90')
                : t('notifications.last90Appear')
            }
          />
        ) : (
          <div
            role="list"
            aria-label={t('notifications.list')}
            className="flex flex-col gap-1"
          >
            {page.notifications.map((notification) => (
              <NotificationRow
                key={notification.id}
                notification={notification}
                onToggleRead={(id, read) => setRead.mutate({ id, read })}
                onOpenNotification={async (row) => {
                  if (!row.read) {
                    await setRead.mutateAsync({ id: row.id, read: true });
                  }
                }}
                onNavigate={(href) => {
                  if (isUnreachableBoard(notification)) {
                    setAccessRefused(true);
                    return;
                  }
                  setAccessRefused(false);
                  window.location.assign(href);
                }}
              />
            ))}
          </div>
        )}

        {page !== undefined ? (
          page.nextCursor === null ? (
            <p className="px-2 py-3 text-center text-xs text-ink-muted">
              {t('notifications.endLast90')}
            </p>
          ) : (
            <p className="px-2 py-3 text-center text-xs text-ink-muted">
              {loadingMore
                ? t('notifications.loadingOlder')
                : t('notifications.moreHistory')}
            </p>
          )
        ) : null}
      </div>
    </div>
  );
}
